import { Clock, Coffee, MapPin, ShoppingCart, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { addItem } from "../../../Redux/State-slice/CartSlice";
import useDistanceLabel from "../../../customHooks/useDistanceLabel";
import { FOOD_TYPE, FOOD_TYPE_LABELS, foodTimeLeftMs } from "../../../services/normalizers";

const CARD_HEIGHT = 200;
const IMAGE_HEIGHT = 110;

function useFoodCountdown(food) {
  const [timeLeft, setTimeLeft] = useState("--:--");
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    const calc = () => {
      const diff = foodTimeLeftMs(food);
      // No window at all (the API gives no expiresAt) — never expires.
      if (diff === null) { setTimeLeft("Available"); setExpired(false); return; }
      if (diff <= 0) { setTimeLeft("Expired"); setExpired(true); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setTimeLeft(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
      setExpired(false);
    };
    calc();
    const t = setInterval(calc, 60000);
    return () => clearInterval(t);
  }, [food]);
  return { timeLeft, expired };
}

function calcPrice(item) {
  return (
    item?.price -
    (item?.discountType === "percentage"
      ? item?.price * (item?.discountValue / 100)
      : item?.discountValue || 0)
  )?.toFixed(0);
}

/* ── Shared card shell ── */
function FoodCardShell({ item, accentColor, badge, infoLine, hovered, onCartClick, showCart = true }) {
  const distanceLabel = useDistanceLabel(item?.location?.lat, item?.location?.long);

  return (
    <motion.div
      style={{
        height: CARD_HEIGHT,
        borderRadius: 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Inter', sans-serif",
        cursor: "pointer",
        background: "#fff",
        boxShadow: hovered
          ? `0 8px 24px ${accentColor}33`
          : "0 2px 8px rgba(0,0,0,0.07)",
        transition: "box-shadow 0.25s",
      }}
    >
      {/* ── Image top ── */}
      <div style={{ height: IMAGE_HEIGHT, flexShrink: 0, position: "relative", overflow: "hidden" }}>
        <motion.img
          src={item?.image || "/placeholder.svg"}
          alt={item?.name}
          animate={{ scale: hovered ? 1.07 : 1 }}
          transition={{ type: "spring", stiffness: 240, damping: 22 }}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />

        {/* Type badge — top left */}
        <div style={{
          position: "absolute", top: 9, left: 9,
          display: "flex", alignItems: "center", gap: 5,
          background: `${accentColor}ee`,
          borderRadius: 20, padding: "3px 9px",
          backdropFilter: "blur(4px)",
        }}>
          {badge.dot
            ? <span style={{ width: 6, height: 6, borderRadius: "50%", background: badge.dotColor, flexShrink: 0 }} />
            : badge.icon}
          <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}>
            {badge.label}
          </span>
        </div>

        {/* Discount — top right */}
        {item?.discountValue > 0 && (
          <div style={{
            position: "absolute", top: 9, right: 9,
            background: accentColor, borderRadius: 20,
            padding: "3px 8px", fontSize: 10, fontWeight: 700, color: "#fff",
          }}>
            {item.discountValue}{item.discountType === "percentage" ? "%" : "TK"} OFF
          </div>
        )}

        {/* Distance — bottom left */}
        {distanceLabel && (
          <div style={{
            position: "absolute", bottom: 7, left: 9,
            display: "flex", alignItems: "center", gap: 3,
            background: "rgba(0,0,0,0.55)", borderRadius: 20,
            padding: "3px 8px", backdropFilter: "blur(4px)",
          }}>
            <MapPin size={10} color="#fff" />
            <span style={{ fontSize: 10, fontWeight: 600, color: "#fff" }}>
              {distanceLabel}
            </span>
          </div>
        )}
      </div>

      {/* ── White content bottom ── */}
      <div style={{
        flex: 1,
        padding: "8px 12px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        overflow: "hidden",
      }}>
        {/* Name */}
        <Link to={`/food-details/${item?._id}`} style={{ textDecoration: "none" }}>
          <p style={{
            fontSize: 13, fontWeight: 700, color: "#111827",
            margin: "0 0 3px", lineHeight: 1.3,
            overflow: "hidden", display: "-webkit-box",
            WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
          }}>
            {item?.name}
          </p>
        </Link>

        {/* Middle info line — varies per type */}
        {infoLine}

        {/* Price + cart */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>৳{calcPrice(item)}</span>
            {item?.discountValue > 0 && (
              <span style={{ fontSize: 11, color: "#d1d5db", textDecoration: "line-through" }}>
                ৳{item?.price?.toFixed(0)}
              </span>
            )}
          </div>
          {showCart && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.88 }}
              onClick={onCartClick}
              style={{
                width: 32, height: 32, borderRadius: "50%", border: "none",
                backgroundColor: accentColor, color: "#fff", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <ShoppingCart size={14} strokeWidth={2.2} />
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────
   READY TO EAT  — blue, countdown
   ───────────────────────────────── */
function InstantCard({ item }) {
  const [hovered, setHovered] = useState(false);
  const dispatch = useDispatch();
  const { timeLeft, expired } = useFoodCountdown(item);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <FoodCardShell
        item={item}
        accentColor="#0d6efd"
        hovered={hovered}
        badge={{
          icon: <Zap size={11} fill="#fff" color="#fff" strokeWidth={0} />,
          label: FOOD_TYPE_LABELS[FOOD_TYPE.READY_TO_EAT],
        }}
        infoLine={
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: expired ? "#9ca3af" : "#0d6efd", fontWeight: 600 }}>
            <Clock size={11} />
            <span>{timeLeft}</span>
            {!expired && <span style={{ color: "#9ca3af", fontWeight: 400 }}>left</span>}
          </div>
        }
        showCart={!expired}
        onCartClick={() => { dispatch(addItem(item)); toast.success("Added to cart!"); }}
      />
    </div>
  );
}

/* ─────────────────────────────────
   PRE-ORDER  — amber, waiting time
   ───────────────────────────────── */
function PreOrderCard({ item }) {
  const [hovered, setHovered] = useState(false);
  const dispatch = useDispatch();

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <FoodCardShell
        item={item}
        accentColor="#f59e0b"
        hovered={hovered}
        badge={{
          icon: <Coffee size={11} color="#fff" strokeWidth={2} />,
          label: "Pre-Order",
        }}
        infoLine={
          item?.waitingTime
            ? (
              <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#92400e", fontWeight: 600 }}>
                <Clock size={11} color="#f59e0b" />
                Ready in {item.waitingTime}
              </div>
            )
            : (
              <p style={{
                fontSize: 11, color: "#9ca3af", margin: 0, lineHeight: 1.4,
                overflow: "hidden", display: "-webkit-box",
                WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
              }}>
                {item?.description}
              </p>
            )
        }
        onCartClick={() => { dispatch(addItem(item)); toast.success("Added to cart!"); }}
      />
    </div>
  );
}

/* ─────────────────────────────────
   E-SHOP  — green, available now
   ───────────────────────────────── */
function ReadyMadeCard({ item }) {
  const [hovered, setHovered] = useState(false);
  const dispatch = useDispatch();

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <FoodCardShell
        item={item}
        accentColor="#059669"
        hovered={hovered}
        badge={{
          dot: true,
          dotColor: "#6ee7b7",
          label: FOOD_TYPE_LABELS[FOOD_TYPE.E_SHOP],
        }}
        infoLine={
          <p style={{
            fontSize: 11, color: "#9ca3af", margin: 0, lineHeight: 1.4,
            overflow: "hidden", display: "-webkit-box",
            WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
          }}>
            {item?.description}
          </p>
        }
        onCartClick={() => { dispatch(addItem(item)); toast.success("Added to cart!"); }}
      />
    </div>
  );
}

/* ── Main export ── */
export default function SellerFoodCard({ item }) {
  if (item?.type === FOOD_TYPE.READY_TO_EAT) return <InstantCard item={item} />;
  if (item?.type === FOOD_TYPE.PRE_ORDER)    return <PreOrderCard item={item} />;
  return <ReadyMadeCard item={item} />;
}
